import styles from './index.module.scss';
import SiteTopNav from '@/components/SiteTopNav';
import img_pages_home_lanhu_1 from '@/assets/images/pages-home-lanhu-1.png';
import img_pages_home_lanhu_2 from '@/assets/images/pages-home-lanhu-2.png';
import img_pages_home_lanhu_3 from '@/assets/svg/pages-home-lanhu-3.svg';
import img_pages_home_lanhu_4 from '@/assets/images/pages-home-lanhu-4.png';
import img_pages_home_lanhu_5 from '@/assets/svg/pages-home-lanhu-5.svg';
import { useNavigate } from 'react-router-dom';
import { useMemo } from 'react';
import { useProductStore, toRouteKey } from '@/store/product';

const isProd = import.meta.env.PROD;
const cdnDomain = String(import.meta.env.VITE_CDN_DOMAIN ?? '').replace(/\/+$/, '');
const localHeroVideoSrc = '/src/assets/video/home-video1.mp4';
const localUspVideoSrc = '/src/assets/video/home-video2.mp4';
const heroVideoSrc =
  isProd && cdnDomain ? `${cdnDomain}/assets/video/home-video1.mp4` : localHeroVideoSrc;
const uspVideoSrc =
  isProd && cdnDomain ? `${cdnDomain}/assets/video/home-video2.mp4` : localUspVideoSrc;

const NEW_ARRIVAL_PRODUCTS = [
  {
    name: ['Misty', 'Harbor'],
    image: `${cdnDomain}/products/Summer%20Haze%20Collection/Misty%20Harbor/Misty%20Harbor1.jpg`,
  },
  {
    name: ['Golden', 'Wheat'],
    image: `${cdnDomain}/products/Summer%20Haze%20Collection/Golden%20Wheat/Golden%20Wheat1.jpg`,
  },
  {
    name: ['Sunset', 'Glow'],
    image: `${cdnDomain}/products/Summer%20Haze%20Collection/Sunset%20Glow/Sunset%20Glow3.jpg`,
  },
  {
    name: ['Ivory', 'Grain'],
    image: `${cdnDomain}/products/Summer%20Haze%20Collection/Ivory%20Grain/Ivory%20Grain1.jpg`,
  },
  {
    name: ['Maple', 'Grove'],
    image: `${cdnDomain}/products/Summer%20Haze%20Collection/Maple%20Grove/Maple%20Grove1.jpeg`,
  },
  {
    name: ['Golden', 'Hour'],
    image: `${cdnDomain}/products/Summer%20Haze%20Collection/Golden%20Hour/Golden%20Hour1.jpeg`,
  },
  {
    name: ['Buttered', 'Wheat'],
    image: `${cdnDomain}/products/Spring%20Bloom%20Collection/Buttered%20Wheat/Buttered%20Wheat1.jpg`,
  },
  {
    name: ['Harvest', 'Moon'],
    image: `${cdnDomain}/products/Spring%20Bloom%20Collection/Harvest%20Moon/Harvest%20Moon1.jpg`,
  },
];

const Home = () => {
  const navigate = useNavigate();
  const collections = useProductStore(s => s.collections);
  const productRouteMap = useMemo(() => {
    const normalize = value =>
      String(value ?? '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ');
    const map = new Map();
    for (const collection of collections ?? []) {
      const collectionKey = collection.routeKey ?? toRouteKey(collection.collection_name);
      if (!collectionKey) continue;
      for (const product of collection.products ?? []) {
        const productKey = product.routeKey ?? toRouteKey(product.product_name);
        const productName = normalize(product.product_name);
        if (!productKey || !productName || map.has(productName)) continue;
        map.set(productName, `/products/${collectionKey}/${productKey}`);
      }
    }
    return map;
  }, [collections]);
  const handleArrivalClick = nameLines => {
    const productName = nameLines.join(' ').trim().toLowerCase().replace(/\s+/g, ' ');
    const targetPath = productRouteMap.get(productName);
    if (targetPath) navigate(targetPath);
  };

  return (
    <div className={styles['home-page']}>
      <div className={styles.page}>
        {/* banner —— top */}
        <section className={`${styles['hero-section']} flex-col`}>
          <video
            className={styles['hero-video']}
            src={heroVideoSrc}
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
          >
            <source src={heroVideoSrc} type="video/mp4" />
          </video>
          <div className={`${styles['hero-overlay']} flex-row align-center justify-center`}>
            <SiteTopNav />

            <div className={styles['hero-content']}>
              <h1 className={styles['hero-title']} data-motion="hero">
                Tradition Meets Innovation <br /> in Every Plank
              </h1>
              <p className={styles['hero-subtitle']} data-motion="copy">
                Timeless Wood. Thoughtfully Crafted. <br />
                Naturally Refined.
              </p>
              <button
                className={`${styles['hero-cta']} flex-row`}
                type="button"
                onClick={() => navigate('/products')}
                data-motion="card-soft"
                data-motion-interactive="press"
              >
                <span className={styles['hero-cta-label']}>view products</span>
                <img
                  className={styles['hero-cta-icon']}
                  alt=""
                  referrerPolicy="no-referrer"
                  src={img_pages_home_lanhu_5}
                />
              </button>
            </div>
          </div>
        </section>

        {/* why sakura floors */}
        <section
          className={styles['features-section']}
          data-motion="section"
          data-motion-threshold="0"
          data-motion-root-margin="0px 0px 0px 0px"
        >
          <h2
            className={styles['features-heading']}
            data-motion="title"
            data-motion-threshold="0"
            data-motion-root-margin="0px 0px 0px 0px"
          >
            why sakura floors
          </h2>
          <div className={styles['features-grid']}>
            <article
              className={`${styles['feature-card']} flex-col`}
              data-motion="card"
              data-motion-threshold="0"
              data-motion-root-margin="0px 0px 0px 0px"
            >
              <img
                className={styles['feature-image']}
                alt=""
                referrerPolicy="no-referrer"
                src={img_pages_home_lanhu_1}
              />
              <h3 className={styles['feature-title']}>Design in California</h3>
              <p className={styles['feature-desc']}>
                A relaxed aesthetic that effortlessly juxtaposes Mediterranean tones and natural
                materials, resulting in light, airy, welcoming spaces. It is more than just a
                flooring, it&apos;s a lifestyle that embraces a relaxed, informal approach to
                living. It&apos;s about creating spaces that are both beautiful and functional,
                promoting well-being and a connection with nature.
              </p>
            </article>
            <article
              className={`${styles['feature-card']} flex-col`}
              data-motion="card"
              data-motion-threshold="0"
              data-motion-root-margin="0px 0px 0px 0px"
            >
              <img
                className={styles['feature-image']}
                alt=""
                referrerPolicy="no-referrer"
                src={img_pages_home_lanhu_2}
              />
              <h3 className={styles['feature-title']}>Guaranteed Quality</h3>
              <p className={styles['feature-desc']}>
                Our finishing and pre-shipment quality control are meticulously conducted at our
                state-of-the-art facilities in Japan. Here, the fusion of traditional craftsmanship
                with cutting-edge technology ensures that each plank meets the highest standards.
              </p>
            </article>
            <article
              className={`${styles['feature-card']} flex-col`}
              data-motion="card"
              data-motion-threshold="0"
              data-motion-root-margin="0px 0px 0px 0px"
            >
              <img
                className={styles['feature-image']}
                alt=""
                referrerPolicy="no-referrer"
                src={img_pages_home_lanhu_4}
              />
              <h3 className={styles['feature-title']}>Wide Product Range</h3>
              <p className={styles['feature-desc']}>
                From the rich warmth of Oak to the timeless elegance of Hickory, our diverse product
                lines span a wide range of sizes and finishes. From 3/8″ to 5/8″, and from 3″ to
                9.5″, there's a perfect fit for every space and style.
              </p>
            </article>
          </div>
        </section>

        {/* banner —— what makes us unique */}
        <section className={`${styles['usp-banner']} flex-col`}>
          <video
            className={styles['usp-video']}
            src={uspVideoSrc}
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
          >
            <source src={uspVideoSrc} type="video/mp4" />
          </video>
          <div className={`${styles['usp-overlay']} flex-row`}>
            <div className={styles['usp-content']}>
              <h2 className={styles['usp-title']} data-motion="title">
                What Makes Us Unique
              </h2>
              <p className={styles['usp-text']} data-motion="copy">
                We create floors without constraint, offering true customization across dimensions,
                species, patterns, and colors—each made to order and shaped precisely to your
                vision. Through innovation and meticulous finishing, European white oak is
                reimagined with refined grain, subtle texture, and quiet consistency inspired by
                Japanese design principles, resulting in a surface that feels calm, balanced, and
                effortlessly sophisticated. Every plank is carefully considered, from grain
                selection to tonal harmony, reflecting a disciplined philosophy of craftsmanship
                defined by precision, restraint, and enduring elegance.
              </p>
            </div>
          </div>
        </section>

        {/* new arrivals — intro constrained; grid full-bleed */}
        <section className={`${styles['new-arrivals-section']} flex-col`}>
          <div className={styles['layout-container']}>
            <div
              className={`${styles['arrivals-intro']} flex-row justify-between`}
              data-motion="section"
            >
              <span className={styles['arrivals-title']}>
                New
                <br />
                Arrivals .
              </span>
              <span className={styles['arrivals-desc']}>
                Experience the perfect harmony of nature&apos;s beauty and modern innovation for a
                healthier, more beautiful home.
                <br />
                Redefining contemporary living with the rich textures and authentic character of
                premium hardwood.
              </span>
            </div>
          </div>
          <div className={styles['product-grid']}>
            {NEW_ARRIVAL_PRODUCTS.map((p, idx) => (
              <article
                key={`${p.name.join('-')}-${idx}`}
                className={styles['product-card']}
                data-motion="card"
                data-motion-group="new-arrivals-grid"
                onClick={() => handleArrivalClick(p.name)}
                style={{ backgroundImage: `url(${p.image})` }}
              >
                <h3 className={styles['product-name']} title={p.name.join(' ')}>
                  {p.name.map((line, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <span key={`${line}-${i}`} className={styles['product-name-line']}>
                      {line}
                      {i < p.name.length - 1 ? <br /> : null}
                    </span>
                  ))}
                </h3>

                <button
                  className={styles['product-cta']}
                  type="button"
                  onClick={event => {
                    event.stopPropagation();
                    handleArrivalClick(p.name);
                  }}
                  data-motion-interactive="press"
                >
                  <div className={styles['product-link']}>
                    view
                    <br />
                    more
                  </div>
                  <div className={styles['product-link-icon']} aria-hidden="true">
                    <img
                      className={styles['product-link-arrow']}
                      alt=""
                      referrerPolicy="no-referrer"
                      src={img_pages_home_lanhu_3}
                    />
                  </div>
                </button>
              </article>
            ))}
          </div>
        </section>

        <section
          className={`${styles['about-section']} ${styles['layout-container']} flex-col justify-between`}
        >
          <h2 className={styles['about-title']} data-motion="title">
            about Sakura floors
          </h2>
          <p className={styles['about-text']} data-motion="copy">
            With years of expertise and experience, Sakura Floors is proud to be regarded as a
            manufacturing leader. Every decision is inspired by our main mission, which is to
            provide the highest standard of product and services to both retailers and customers..
          </p>
        </section>
      </div>
    </div>
  );
};

export default Home;
